// Nombre legible de las variables de loan-config que el mock no dibujó.
// La auditoría lo usa para nombrar esos cambios; sin entrada, cae a la key legible.

export interface ExtraMeta {
    /** Nombre legible (es-MX). */
    name: string;
}

/** Clave = `paramType#key` (formato wire). */
export const EXTRA_META: { readonly [id: string]: ExtraMeta | undefined } = {
    // ---------- ANTI_FRAUD ----------
    'ANTI_FRAUD#avg_window_days': { name: 'Ventana del promedio de cobros' },
    // ---------- COLLECTIONS ----------
    'COLLECTIONS#auto_message_hours': { name: 'Horario de mensajes automáticos' },
    'COLLECTIONS#contact_hours': { name: 'Horario de contacto permitido' },
    'COLLECTIONS#days_without_contact_alert': { name: 'Días sin contacto antes de alerta' },
    'COLLECTIONS#escalate_to_senior_days': { name: 'Días de mora para escalar a senior' },
    'COLLECTIONS#max_auto_messages_per_day': { name: 'Mensajes automáticos por día (máx.)' },
    'COLLECTIONS#max_load_per_collector': { name: 'Carga máxima por cobrador' },
    // ---------- CREDIT ----------
    'CREDIT#application_discard_days': { name: 'Días para descartar solicitud' },
    'CREDIT#max_restructures_per_credit': { name: 'Reestructuras máximas por crédito' },
    'CREDIT#opening_commission_pct': { name: 'Comisión de apertura' },
    'CREDIT#voluntary_cancellation_days': { name: 'Días para cancelación voluntaria' },
    'CREDIT#restructure_window_min_days': { name: 'Mora mínima para proponer reestructura' },
    'CREDIT#restructure_window_max_days': { name: 'Mora máxima para proponer reestructura' },
    'CREDIT#restructure_rate_increase_min_pct': { name: 'Alza mínima de la TNA en reestructura' },
    'CREDIT#restructure_rate_increase_max_pct': { name: 'Alza máxima de la TNA en reestructura' },
    'CREDIT#restructure_initial_pct': { name: 'Pago inicial de la reestructura' },
    'CREDIT#bank_holidays': { name: 'Días inhábiles bancarios (CNBV)' },
    // ---------- CROSS_BORDER ----------
    'CROSS_BORDER#uncollected_order_cancel_days': { name: 'Días para cancelar orden no recogida' },
    // ---------- MORA_BLOCK ----------
    'MORA_BLOCK#daily_pcf_attempts': { name: 'Intentos de débito PCF por día' },
    'MORA_BLOCK#mora_batch_hour': { name: 'Hora del batch de mora' },
    'MORA_BLOCK#reconciliation_batch_hour': { name: 'Hora del batch de conciliación' },
    'MORA_BLOCK#second_pcf_attempt_hour': { name: 'Hora del segundo intento PCF' },
    'MORA_BLOCK#sic_report_days': { name: 'Días de mora para reporte a SIC' },
    'MORA_BLOCK#t_plus_n_days': { name: 'Bloqueo T+N tras vencimiento' },
    // ---------- NOTIFICATIONS ----------
    'NOTIFICATIONS#auto_message_cutoff_day': { name: 'Día de corte de mensajes automáticos' },
    'NOTIFICATIONS#pre_due_reminder': { name: 'Recordatorio pre-vencimiento (código)' },
    'NOTIFICATIONS#sic_warning_day': { name: 'Día del aviso de reporte a SIC' },
    'NOTIFICATIONS#sms_on_block': { name: 'SMS de respaldo al bloquear' },
    // ---------- PAY_INVESTIGATION ----------
    'PAY_INVESTIGATION#investigation_sla_hours': { name: 'SLA de investigación de pago' },
    'PAY_INVESTIGATION#max_unconfirmed_investigations': {
        name: 'Investigaciones no confirmadas (máx. anti-abuso)',
    },
    'PAY_INVESTIGATION#unblock_on_open': { name: 'Desbloqueo inmediato al abrir investigación' },
    // ---------- REMITTANCE ----------
    'REMITTANCE#frequency': { name: 'Periodicidad de remisión a VentasLuga' },
    // ---------- UNBLOCK ----------
    'UNBLOCK#overdue_1_2_rule': { name: 'Regla con 1-2 cuotas vencidas' },
    'UNBLOCK#overdue_3_plus_rule': { name: 'Regla con 3+ cuotas vencidas' },
    'UNBLOCK#recurrent_3_plus_rule': { name: 'Regla para reincidentes (3+ atrasos)' },
    // ---------- WARRANTY ----------
    'WARRANTY#extended_warranty_cost_cents': { name: 'Costo de la garantía extendida' },
    'WARRANTY#warranty_deductible_cents': { name: 'Deducible de la garantía' },
    'WARRANTY#max_substitute_tier': { name: 'Gama máxima del equipo sustituto' },
    'WARRANTY#max_uses_per_credit': { name: 'Usos máximos por crédito' },
    // ---------- WIZARD (los que el mock no dibujó) ----------
    'WIZARD#otp_digits': { name: 'Dígitos del código OTP' },
    'WIZARD#contract_otp_timeout_seconds': { name: 'Expiración del OTP de firma de contrato' },
    'WIZARD#kyc_metamap_retries': { name: 'Reintentos de KYC (Metamap)' },
};

/** Etiquetas es-MX de las categorías del backend. */
export const EXTRA_CATEGORY_LABEL: { readonly [paramType: string]: string | undefined } = {
    ANTI_FRAUD: 'Anti-fraude',
    COLLECTIONS: 'Cobranza',
    CREDIT: 'Crédito',
    CROSS_BORDER: 'Cross-border',
    MORA_BLOCK: 'Mora y bloqueo',
    NOTIFICATIONS: 'Notificaciones',
    PAY_INVESTIGATION: 'Investigación de pago',
    REMITTANCE: 'Remisión VentasLuga',
    SCORING: 'Motor de crédito',
    UNBLOCK: 'Desbloqueo',
    WARRANTY: 'Garantía extendida',
    WIZARD: 'Wizard',
    INTEGRATIONS: 'Integraciones',
    FEATURE_FLAG: 'Feature flags',
};

/** Fallback para variables futuras sin entrada en el catálogo: key prettificada. */
export function prettifyKey(key: string): string {
    const words = key.replaceAll('_', ' ');
    return words.charAt(0).toUpperCase() + words.slice(1);
}
